type HeadingSelector = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

type UseHeadingsReturn = {
  headings: Ref<{ id: string; text: string }[]>;
  activeHeading: Ref<string>;
};

/**
 * Fetch data from Strapi REST API
 * @param selector - Heading selector (h1 to h6)
 * @param templateRef - Vue template ref of content including headings
 * @returns An object containing all headings and the first visible one in the template ref
 */
export function useHeadings(
  selector: HeadingSelector,
  templateRef: Ref<HTMLElement | undefined>
): UseHeadingsReturn {
  const headingEls = computed(
    () => templateRef?.value?.querySelectorAll(selector) || []
  );

  const headings = computed(() =>
    [...headingEls.value].map((heading: HTMLElement) => ({
      id: heading.id,
      text: heading.innerText,
    }))
  );
  const activeHeading = ref();

  const { height } = useWindowSize();

  watch(
    [headingEls, height],
    () => {
      if (!activeHeading.value) {
        activeHeading.value = headings.value?.[0]?.id;
      }

      const isSupported =
        typeof window !== 'undefined' && 'IntersectionObserver' in window;
      if (!isSupported) return;

      const callback = (entries: IntersectionObserverEntry[]): void => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            activeHeading.value = entry.target.id;
          } else {
            // Exits the screen by the bottom
            if (entry.boundingClientRect.top > 0) {
              // Select the previous heading element as active
              const previousHeadingIndex =
                headings.value.findIndex(
                  heading => heading.id === entry.target.id
                ) - 1;

              if (
                previousHeadingIndex > -1 &&
                activeHeading.value === entry?.target.id
              ) {
                activeHeading.value = headings.value[previousHeadingIndex].id;
              }
            }
          }
        }
      };

      const rootMarginBottomValue = Math.max(height.value - 176, 0);
      const observer = new IntersectionObserver(callback, {
        // We want to observe headings position when they pass the toc
        rootMargin: `0px 0px -${rootMarginBottomValue}px 0px`,
      });
      headingEls.value.forEach(headingEl => observer.observe(headingEl));
    },
    { immediate: true }
  );

  return { headings, activeHeading };
}
